import type { RawEditorOptions } from "tinymce";

export interface RichTextEditorOptionInputs {
    /** The contenteditable element the editor runs on. */
    target: HTMLElement;
    /** The page's UI language code. */
    language: string;
    /** The hint TinyMCE shows while the editor is empty, or an empty string for none. */
    placeholder: string;
    /** The container the toolbar docks in, or null for an editor with no toolbar. */
    toolbarContainer: HTMLElement | null;
    /** Uploads one image and resolves with its address, or null when the page offers no upload. */
    uploadHandler: ((blobInfo: { blob: () => Blob; filename: () => string }) => Promise<string>) | null;
}

/**
 * The `tinymce.init` options the rich-text editor controller boots with, bar `setup`. The
 * controller's class comment says why the editor runs inline, why it writes no hidden input of its
 * own, and why it configures no content CSS.
 */
export function richTextEditorOptions({ target, language, placeholder, toolbarContainer, uploadHandler }: RichTextEditorOptionInputs): RawEditorOptions {
    const hasUpload = uploadHandler !== null;
    return {
        target,
        inline: true,
        license_key: "gpl",
        // Both suppress TinyMCE CSS injection into the host document; see the controller's class comment.
        skin: false,
        content_css: false,
        menubar: false,
        // The pack comes with the editor module; English stays TinyMCE's built-in, so it passes none.
        ...(language && language !== "en" ? { language } : {}),
        // TinyMCE marks the empty editable and the suite's own stylesheet draws the text; see Libraries/src/mvdmio.ASP.Razor/wwwroot/js/rich-text-editor-skin.input.css.
        ...(placeholder ? { placeholder } : {}),
        toolbar: toolbarContainer
            ? "undo redo | blocks | bold italic underline strikethrough | link image media table | bullist numlist outdent indent | code | help"
            : false,
        // Docks the toolbar in the component's own container and keeps it there on blur.
        // Both options are inline-mode-only; they are inert for an iframed editor.
        ...(toolbarContainer
            ? { fixed_toolbar_container_target: toolbarContainer, toolbar_persist: true }
            : {}),
        plugins: [
            "advlist", "autolink", "lists", "link", "image", "charmap", "anchor",
            "searchreplace", "code", "insertdatetime", "media", "table", "help", "wordcount",
        ],
        // extended_valid_elements replaces the span rule, so style has to be named or a
        // text-decoration underline span is stripped on load.
        extended_valid_elements: "span[class|style|data-person-id]",
        formats: {
            underline: [
                { inline: "u", exact: true },
                { inline: "span", styles: { textDecoration: "underline" }, exact: true },
            ],
        },
        promotion: false,
        branding: false,
        // This controller is the single writer of the hidden input; see the controller's class comment.
        hidden_input: false,
        add_form_submit_trigger: false,
        images_upload_handler: uploadHandler ?? undefined,
        // The image dialog's Upload tab. The General tab keeps insert-by-web-address.
        image_uploadtab: hasUpload,
        automatic_uploads: hasUpload,
        // Keep every link and image address as written, with or without an upload endpoint.
        // TinyMCE's default rewrites a root-relative address (`/Policies/<id>`, an uploaded
        // `/images/file/...`) to a page-relative one on every save, which breaks it on a page
        // at a different depth.
        convert_urls: false,
        // `.svg` is on the picker list so the dialog posts it. The default list drops
        // that extension before the request, and the member never sees the server's reason.
        ...(hasUpload
            ? { images_file_types: "jpeg,jpg,jpe,jfi,jif,jfif,png,gif,bmp,webp,svg" }
            : {}),
    };
}
